import { usePersistedState } from "@/lib/persistedFilters";
// Helpdesk (KEITH) — Iter 108v refactored.
//
// Kanban board of tickets grouped by status, with a bottom tab bar
// that pins every ticket the user opens so they can jump between
// multiple active conversations. Reference: Clearfeed / HappyFox /
// Zendesk.
//
// Iter 108v — This file used to be 1265 lines with 5+ components
// sharing the same scope, which made hook ordering unpredictable and
// churn expensive. The page-level component is now the sole occupant
// of this file. Every leaf component lives under
// `/src/components/helpdesk/`:
//   • TicketCard.jsx        — Kanban card
//   • KanbanBoard.jsx       — the four-column board
//   • TicketDetail.jsx      — detail view + timeline + AI panel
//   • NewTicketDialog.jsx   — creation modal
//   • DeleteTicketDialog.jsx — soft-delete confirmation
//   • helpdeskUtils.js      — shared constants + fmt helpers
//
// P1 scope: create/list/patch tickets, drag-drop status changes,
// reply / internal-note composer, activity events sidebar.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { LayoutGrid, Plus, X } from "lucide-react";
import { COLUMNS, PRIORITIES, loadTabs, saveTabs } from "@/components/helpdesk/helpdeskUtils";
import { KanbanBoard } from "@/components/helpdesk/KanbanBoard";
import { TicketDetail } from "@/components/helpdesk/TicketDetail";
import { NewTicketDialog } from "@/components/helpdesk/NewTicketDialog";
import { DeleteTicketDialog } from "@/components/helpdesk/DeleteTicketDialog";

export default function Helpdesk() {
  const { has } = useAuth();
  const navigate = useNavigate();
  const { ticketId: routeTicketId } = useParams();

  const [tickets, setTickets] = useState([]);
  const [agents, setAgents]   = useState([]);
  const [teams, setTeams]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAssignee, setFilterAssignee] = usePersistedState("helpdesk.assignee", "all"); // all|me|unassigned|<uid>
  const [filterPriority, setFilterPriority] = usePersistedState("helpdesk.priority", "all");
  const [filterTeam, setFilterTeam]         = usePersistedState("helpdesk.team", "all");
  const [search, setSearch]   = usePersistedState("helpdesk.search", "");
  const [openTabs, setOpenTabs] = useState(loadTabs());
  const [activeTab, setActiveTab] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [filterStatus, setFilterStatus] = usePersistedState("helpdesk.status", "all");
  const [, setTick] = useState(0);

  const canAgent  = has("helpdesk.edit");
  const canManage = has("helpdesk.edit");

  // Re-render every 30s so SLA countdowns tick.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const [searchParams] = useSearchParams();
  const orgFilter  = searchParams.get("org");
  const userFilter = searchParams.get("user");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAssignee && filterAssignee !== "all") params.set("assignee", filterAssignee);
      if (filterPriority && filterPriority !== "all") params.set("priority", filterPriority);
      if (filterStatus   && filterStatus   !== "all") params.set("status",   filterStatus);
      if (filterTeam     && filterTeam     !== "all") params.set("team",     filterTeam);
      if (search)     params.set("q", search);
      if (orgFilter)  params.set("requester_org_id",  orgFilter);
      if (userFilter) params.set("requester_user_id", userFilter);
      const [t, a, tm] = await Promise.all([
        api.get(`/helpdesk/tickets?${params.toString()}`),
        api.get("/helpdesk/agents"),
        api.get("/helpdesk/teams"),
      ]);
      setTickets(t.data.tickets || []);
      setAgents(a.data.agents || []);
      setTeams(tm.data.teams || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterAssignee, filterPriority, filterTeam, filterStatus, orgFilter, userFilter]);

  // Deep link support (/helpdesk/:ticketId auto-opens the tab).
  useEffect(() => {
    if (routeTicketId) openTicket({ ticket_id: routeTicketId });
  }, [routeTicketId]);

  useEffect(() => { saveTabs(openTabs); }, [openTabs]);

  const openTicket = (t) => {
    setOpenTabs((prev) => {
      if (prev.some((x) => x.ticket_id === t.ticket_id)) return prev;
      return [...prev, { ticket_id: t.ticket_id, cf_id: t.cf_id, subject: t.subject }];
    });
    setActiveTab(t.ticket_id);
  };
  const closeTab = (tid) => {
    setOpenTabs((prev) => prev.filter((x) => x.ticket_id !== tid));
    setActiveTab((cur) => (cur === tid ? null : cur));
  };

  const columns = useMemo(() => {
    const bucket = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    (tickets || []).forEach((t) => {
      if (bucket[t.status]) bucket[t.status].push(t);
    });
    return bucket;
  }, [tickets]);

  // ----- Drag & drop --------------------------------------------------------
  const dragTicketId = useRef(null);
  const onDragStart = (e, t) => {
    if (!canAgent) return;
    dragTicketId.current = t.ticket_id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onColumnDrop = async (e, status) => {
    e.preventDefault();
    const tid = dragTicketId.current;
    dragTicketId.current = null;
    if (!tid) return;
    const t = tickets.find((x) => x.ticket_id === tid);
    if (!t || t.status === status) return;
    // Optimistic update.
    setTickets((prev) => prev.map((x) => (x.ticket_id === tid ? { ...x, status } : x)));
    try {
      await api.patch(`/helpdesk/tickets/${tid}`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
    } catch {
      toast.error("Could not update status");
      load();
    }
  };

  // ----- Render -------------------------------------------------------------
  const activeTicketId = activeTab;
  const showBoard = !activeTicketId;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col" data-testid="helpdesk-page">
      <PageHeader testid="helpdesk-header" title="Helpdesk — KEITH"
                  subtitle="Kanban ticket board. Drag cards between columns to change status." />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()}
               placeholder="Search subject or CF-ID…" className="h-9 max-w-xs"
               data-testid="helpdesk-search" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="helpdesk-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="helpdesk-assignee-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="helpdesk-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {teams.length > 0 && (
          <Select value={filterTeam} onValueChange={setFilterTeam}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="helpdesk-team-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>Refresh</Button>
        <div className="ml-auto flex items-center gap-2">
          {(orgFilter || userFilter) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-800"
                  data-testid="helpdesk-context-filter">
              Filtered by {orgFilter ? "org" : "user"}
              <button onClick={() => navigate("/helpdesk")}
                      className="ml-1 rounded hover:bg-indigo-200" aria-label="clear filter">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {activeTicketId && (
            <Button variant="outline" size="sm" onClick={() => setActiveTab(null)} data-testid="helpdesk-back-to-board">
              <LayoutGrid className="h-3.5 w-3.5" /> Board
            </Button>
          )}
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="helpdesk-new-ticket">
            <Plus className="h-3.5 w-3.5" /> New ticket
          </Button>
        </div>
      </div>

      {/* Main workspace */}
      <div className="flex-1 overflow-hidden bg-slate-50">
        {showBoard ? (
          <KanbanBoard
            columns={columns}
            agents={agents}
            canAgent={canAgent}
            canManage={canManage}
            onOpenTicket={openTicket}
            onDeleteTicket={(t) => setTicketToDelete(t)}
            onDragStart={onDragStart}
            onColumnDrop={onColumnDrop}
          />
        ) : (
          <TicketDetail key={activeTicketId} ticketId={activeTicketId}
                        agents={agents} teams={teams} canAgent={canAgent}
                        onRefresh={load}
                        onNavigate={(u) => navigate(u)} />
        )}
      </div>

      {/* Bottom tab bar */}
      <div className="flex items-center gap-1 overflow-x-auto border-t border-slate-200 bg-white px-2 py-1"
           data-testid="helpdesk-tabbar">
        <button type="button"
                onClick={() => setActiveTab(null)}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${!activeTicketId ? "bg-primary/10 text-primary" : "text-slate-500 hover:bg-slate-100"}`}
                data-testid="helpdesk-tab-board">
          <LayoutGrid className="h-3.5 w-3.5" /> Board
        </button>
        {openTabs.map((tab) => (
          <div key={tab.ticket_id}
               onClick={() => setActiveTab(tab.ticket_id)}
               onAuxClick={(e) => e.button === 1 && closeTab(tab.ticket_id)}
               className={`group inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs ${activeTab === tab.ticket_id ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100"}`}
               data-testid={`helpdesk-tab-${tab.ticket_id}`}>
            <span className="font-mono text-[10px] text-slate-400">{tab.cf_id}</span>
            <span className="max-w-[16ch] truncate">{tab.subject}</span>
            <span onClick={(e) => { e.stopPropagation(); closeTab(tab.ticket_id); }}
                  className="ml-1 rounded p-0.5 opacity-0 group-hover:opacity-100 hover:bg-slate-200"
                  aria-label="Close tab">
              <X className="h-3 w-3" />
            </span>
          </div>
        ))}
        {openTabs.length === 0 && (
          <span className="ml-2 text-[11px] text-slate-400">Open a ticket to pin it as a tab</span>
        )}
      </div>

      {showCreate && (
        <NewTicketDialog onClose={() => setShowCreate(false)}
                         onCreated={(t) => { setShowCreate(false); load(); openTicket(t); }} />
      )}
      {ticketToDelete && (
        <DeleteTicketDialog ticket={ticketToDelete}
                            onClose={() => setTicketToDelete(null)}
                            onDeleted={() => { setTicketToDelete(null); load(); }} />
      )}
    </div>
  );
}
